import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, Edit2, Info, Key, Loader2, Lock, Plus, Power, PowerOff, ScrollText, Search, ShieldCheck, ShieldOff, Trash2, Unlock, UsersRound, X } from 'lucide-react';
import {
  adminUsers, adminSites, adminPasswordReset,
  type AdminUserRow,
} from '@/api/client';

// Two-option role picker for the Assign-to-site modal — matches the invite
// hierarchy: Site Admin needs a site, Platform Admin is global (no site).
type AssignRole = 'site_admin' | 'platform_admin';
import { humanRole, humanStatus } from '@/lib/humanise';
import { SITE_CHANGE_ROLES, initialSiteRole } from '@/lib/siteRoles';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


/**
 * Admin > Users — ADMIN-tier management only.
 *
 * After the Sprint-1 follow-up: this page no longer lists every user. It
 * shows users with an ADMIN role anywhere — `platform_admin`,
 * `application_admin`, `compliance`, `study_designer`, or `site_admin` at
 * any site. Non-admin site users (PI / coordinator / monitor / etc.) live
 * on the Site Team page, scoped to a single site.
 *
 * The page supports: stats, filters, sort, row expand, full action menu
 * (lock/unlock, enable/disable, reset MFA, send password reset, delete,
 * view audit, change role, add to site, remove from site).
 */

const ROLES = [
  'site_admin', 'principal_investigator', 'sub_investigator', 'coordinator',
  'site_study_designer', 'regulatory_specialist', 'site_pharma',
  'finance_billing', 'monitor', 'auditor',
];

const STATUSES = ['active', 'invited', 'locked', 'disabled'] as const;
type StatusFilter = (typeof STATUSES)[number] | 'all';
type MfaFilter = 'all' | 'yes' | 'no';
type RoleFilter = 'all' | string;
type SiteFilter = 'all' | string;
type SortKey = 'name' | 'status' | 'last_login' | 'created';
type SortDir = 'asc' | 'desc';

function initials(fullName: string, email: string): string {
  const name = (fullName || email.split('@')[0] || '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
}

// Deterministic palette so the same user always gets the same colour without
// pulling in a hashing dep — sum char codes mod palette length.
const AVATAR_COLOURS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-teal-500',
];
function avatarColour(seed: string): string {
  let n = 0;
  for (let i = 0; i < seed.length; i++) n += seed.charCodeAt(i);
  return AVATAR_COLOURS[n % AVATAR_COLOURS.length]!;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return `${Math.floor(diffMo / 12)}y ago`;
}

export function AdminUsersPage() {
  const qc = useQueryClient();

  // Filters + sort + search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [siteFilter, setSiteFilter] = useState<SiteFilter>('all');
  const [mfaFilter, setMfaFilter] = useState<MfaFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('last_login');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Per-user action / modal state
  // Action outcomes go via sonner toasts (not inline banners).
  const navigate = useNavigate();
  const [roleEdit, setRoleEdit] = useState<{ user: AdminUserRow; site_id: string; role: string } | null>(null);
  const [addingSite, setAddingSite] = useState<{ user: AdminUserRow; role: AssignRole; site_id: string } | null>(null);
  const [deletingUser, setDeletingUser] = useState<{ user: AdminUserRow; confirm_email: string } | null>(null);

  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => adminSites.list() });

  const sitesById = useMemo(() => {
    const m = new Map<string, string>();
    sites.data?.sites.forEach((s) => m.set(s.site_id, s.name));
    return m;
  }, [sites.data]);

  // user_id -> {full_name, email} for resolving "Created by" column without
  // a second network call. Built from the same /admin/users payload.
  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    users.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [users.data]);

  // Stats from the admin-tier base set (not affected by current filters).
  const stats = useMemo(() => {
    const ADMIN_ROLES = new Set(['platform_admin', 'application_admin', 'site_admin']);
    const rows = users.data?.rows.filter(
      (u) => u.sites.some((s) => ADMIN_ROLES.has(s.role ?? '')),
    ) ?? [];
    return {
      total: rows.length,
      active: rows.filter((u) => !u.locked_at && u.status === 'active').length,
      invited: rows.filter((u) => u.status === 'invited').length,
      locked: rows.filter((u) => !!u.locked_at).length,
      disabled: rows.filter((u) => u.status === 'disabled').length,
      mfaEnrolled: rows.filter((u) => u.mfa_enrolled).length,
    };
  }, [users.data]);

  // Admin-tier base set: anyone with a platform or site_admin role at this site.
  // Other site roles (PI, coordinator, monitor, etc.) are managed from the Site Team page.
  const ADMIN_ROLES = new Set(['platform_admin', 'application_admin', 'site_admin']);
  const adminRows = useMemo(() => {
    if (!users.data) return [];
    return users.data.rows.filter(
      (u) => u.sites.some((s) => ADMIN_ROLES.has(s.role ?? '')),
    );
  }, [users.data]);

  // Filter + sort
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const rows = adminRows.filter((u) => {
      if (q && !u.email.toLowerCase().includes(q) && !u.full_name.toLowerCase().includes(q)) {
        return false;
      }
      // Status filter
      if (statusFilter !== 'all') {
        const effective = u.locked_at ? 'locked' : u.status;
        if (effective !== statusFilter) return false;
      }
      // MFA filter
      if (mfaFilter === 'yes' && !u.mfa_enrolled) return false;
      if (mfaFilter === 'no' && u.mfa_enrolled) return false;
      // Site filter
      if (siteFilter !== 'all' && !u.sites.some((s) => s.site_id === siteFilter)) return false;
      // Role filter (any of the user's site roles matches)
      if (roleFilter !== 'all' && !u.sites.some((s) => s.role === roleFilter)) return false;
      return true;
    });

    const cmp = (a: AdminUserRow, b: AdminUserRow): number => {
      let v = 0;
      switch (sortKey) {
        case 'name':
          v = (a.full_name || a.email).localeCompare(b.full_name || b.email);
          break;
        case 'status':
          v = (a.status || '').localeCompare(b.status || '');
          break;
        case 'last_login': {
          const at = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
          const bt = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
          v = at - bt;
          break;
        }
        case 'created': {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
          v = at - bt;
          break;
        }
      }
      return sortDir === 'asc' ? v : -v;
    };
    return [...rows].sort(cmp);
  }, [users.data, search, statusFilter, roleFilter, siteFilter, mfaFilter, sortKey, sortDir]);

  const onError = (e: unknown) =>
    toast.error(errorText(e, 'Action failed'));
  const onSuccess = (msg: string) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ['admin-users'] });
  };

  // Mutations
  const lockMut = useMutation({
    mutationFn: (id: string) => adminUsers.lock(id),
    onSuccess: () => onSuccess('User locked.'), onError
  });
  const unlockMut = useMutation({
    mutationFn: (id: string) => adminUsers.unlock(id),
    onSuccess: () => onSuccess('User unlocked.'), onError
  });
  const enableMut = useMutation({
    mutationFn: (id: string) => adminUsers.enable(id),
    onSuccess: () => onSuccess('Account enabled.'), onError
  });
  const disableMut = useMutation({
    mutationFn: (id: string) => adminUsers.disable(id),
    onSuccess: () => onSuccess('Account disabled.'), onError
  });
  const resetMfaMut = useMutation({
    mutationFn: (id: string) => adminUsers.resetMfa(id),
    onSuccess: () => onSuccess('MFA reset — user will re-enrol on next login.'), onError
  });
  const resetPwMut = useMutation({
    mutationFn: (email: string) => adminPasswordReset.send(email),
    onSuccess: () => onSuccess('Password-reset email sent (if account exists).'), onError
  });
  const removeMut = useMutation({
    mutationFn: () => adminUsers.remove(deletingUser!.user.user_id, deletingUser!.confirm_email),
    onSuccess: () => { setDeletingUser(null); onSuccess('User deleted.'); },
    onError,
  });
  const changeRoleMut = useMutation({
    mutationFn: () => adminUsers.changeRole(roleEdit!.user.user_id, roleEdit!.site_id, roleEdit!.role),
    onSuccess: () => { setRoleEdit(null); onSuccess('Role updated.'); }, onError,
  });
  const addSiteMut = useMutation({
    mutationFn: async () => {
      const a = addingSite!;
      if (a.role === 'platform_admin') {
        // Global role — no site coupling. Uses the bootstrap create endpoint
        // which handles existing users by adding the platform delegation.
        return adminUsers.create({
          email: a.user.email,
          full_name: a.user.full_name || undefined,
          role: 'platform_admin',
          send_invite: false,
        });
      }
      // Site Admin path — site-scoped membership + per-site delegation.
      return adminUsers.addSite(a.user.user_id, { site_id: a.site_id, role: a.role });
    },
    onSuccess: (_data, _vars) => {
      const wasGlobal = addingSite?.role === 'platform_admin';
      setAddingSite(null);
      onSuccess(wasGlobal ? 'User promoted to Platform Admin.' : 'User assigned to site.');
    },
    onError,
  });
  const removeSiteMut = useMutation({
    mutationFn: (input: { userId: string; siteId: string }) =>
      adminUsers.removeSite(input.userId, input.siteId),
    onSuccess: () => onSuccess('User removed from site.'), onError,
  });

  function toggleExpand(userId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'last_login' || key === 'created' ? 'desc' : 'asc'); }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return null;
    return <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Users"
        subtitle="Platform admins, application admins, compliance, study designers, and site admins. For site-level team members go to Site Team."
        actions={
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
            <input
              type="text"
              className="input pl-7"
              placeholder="Search email or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      {/* Stats summary */}
      {users.data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="card p-3">
            <div className="eyebrow mb-0.5">Total</div>
            <div className="text-xl font-bold">{stats.total}</div>
          </div>
          <div className="card p-3">
            <div className="eyebrow mb-0.5">Active</div>
            <div className="text-xl font-bold text-success-700">{stats.active}</div>
          </div>
          <div className="card p-3">
            <div className="eyebrow mb-0.5">Invited</div>
            <div className="text-xl font-bold text-blue">{stats.invited}</div>
          </div>
          <div className="card p-3">
            <div className="eyebrow mb-0.5">Locked</div>
            <div className="text-xl font-bold text-danger-700">{stats.locked}</div>
          </div>
          <div className="card p-3">
            <div className="eyebrow mb-0.5">Disabled</div>
            <div className="text-xl font-bold text-ink-muted">{stats.disabled}</div>
          </div>
          <div className="card p-3">
            <div className="eyebrow mb-0.5">MFA Enrolled</div>
            <div className="text-xl font-bold">{stats.mfaEnrolled}</div>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="card p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label text-xs">Status</label>
            <select className="input"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="all">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{humanStatus(s)}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Role</label>
            <select className="input"
              value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Site</label>
            <select className="input"
              value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
              <option value="all">All sites</option>
              {sites.data?.sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">MFA enrolled</label>
            <select className="input"
              value={mfaFilter} onChange={(e) => setMfaFilter(e.target.value as MfaFilter)}>
              <option value="all">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {users.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
          </div>
        )}
        {users.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(users.error)}`}>
            {isPermissionError(users.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(users.error, 'Failed to load users')}</span>
          </div>
        )}
        {users.data && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">
            <UsersRound className="mx-auto mb-2 h-10 w-10 opacity-50" />
            {search || statusFilter !== 'all' || roleFilter !== 'all' || siteFilter !== 'all' || mfaFilter !== 'all'
              ? 'No users match the current filters.'
              : 'No users yet.'}
          </div>
        )}
        {users.data && filtered.length > 0 && (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <span className="inline-flex items-center gap-1">User {sortIndicator('name')}</span>
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="inline-flex items-center gap-1">Status {sortIndicator('status')}</span>
                </th>
                <th className="px-3 py-2">Platform Role</th>
                <th className="px-3 py-2">MFA</th>
                <th className="px-3 py-2">Site roles</th>
                <th className="px-3 py-2 text-center">Sites</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('last_login')}>
                  <span className="inline-flex items-center gap-1">Last login {sortIndicator('last_login')}</span>
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('created')}>
                  <span className="inline-flex items-center gap-1">Created {sortIndicator('created')}</span>
                </th>
                <th className="px-3 py-2">Created by</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isExpanded = expanded.has(u.user_id);
                const verified = !!u.email_verified_at || (!u.locked_at && !!u.last_login_at);
                const creator = u.created_by_user_id ? usersById.get(u.created_by_user_id) : null;
                return (
                  <Fragment key={u.user_id}>
                    <tr className="border-b border-surface-border last:border-0 align-top hover:bg-surface-muted/30">
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => toggleExpand(u.user_id)}
                          className="text-ink-muted hover:text-ink-primary" aria-label="Expand row">
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-8 w-8 shrink-0 rounded-full grid place-items-center text-white text-[10px] font-bold ${avatarColour(u.user_id)}`}>
                            {initials(u.full_name, u.email)}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-1.5">
                              {u.full_name || u.email.split('@')[0]}
                              {verified && (
                                <span title="Email verified">
                                  <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-ink-muted">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {u.locked_at ? (
                          <span className="badge badge-danger">Locked</span>
                        ) : (
                          <span className={u.status === 'active' ? 'badge badge-success' : u.status === 'disabled' ? 'badge badge-warn' : 'badge badge-info'}>
                            {humanStatus(u.status)}
                          </span>
                        )}
                        {u.lock_reason && <div className="mt-1 text-[10px] text-ink-muted">{u.lock_reason}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {u.sites[0]?.role ? (
                          <span className="badge badge-success">{humanRole(u.sites[0].role)}</span>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {u.mfa_enrolled
                          ? <span className="text-success-700">Yes</span>
                          : <span className="text-ink-muted">No</span>}
                      </td>
                      <td className="px-3 py-2">
                        {(u.sites[0]?.role === 'platform_admin' || u.sites[0]?.role === 'application_admin') ? (
                          <span className="text-xs italic text-ink-muted">
                            All sites (Platform Admin)
                          </span>
                        ) : (
                          <>
                            {u.sites.length === 0 ? (
                              <span className="text-xs italic text-ink-muted">No memberships</span>
                            ) : (
                              <ul className="space-y-1">
                                {u.sites.map((s) => (
                                  <li key={s.site_id} className="flex items-center gap-2 text-xs">
                                    <span className="font-medium">{sitesById.get(s.site_id) ?? s.site_id.slice(0, 8)}</span>
                                    <span className="badge badge-info text-[10px]">{s.role ? humanRole(s.role) : 'No role'}</span>
                                    <button
                                      type="button"
                                      className="text-ink-muted hover:text-ink-primary"
                                      onClick={() => setRoleEdit({ user: u, site_id: s.site_id, role: initialSiteRole(s.role, SITE_CHANGE_ROLES) })}
                                      aria-label="Change role"
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </button>
                                    <button
                                      type="button"
                                      className="text-ink-muted hover:text-danger-700"
                                      onClick={() => {
                                        const siteName = sitesById.get(s.site_id) ?? s.site_id.slice(0, 8);
                                        if (window.confirm(`Remove ${u.email} from ${siteName}?`)) {
                                          removeSiteMut.mutate({ userId: u.user_id, siteId: s.site_id });
                                        }
                                      }}
                                      aria-label="Remove from site"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <button
                              type="button"
                              className="mt-1 flex items-center gap-1 text-[10px] text-blue hover:text-navy font-medium"
                              onClick={() => setAddingSite({ user: u, role: 'site_admin', site_id: '' })}
                            >
                              <Plus className="h-3 w-3" /> Add to site
                            </button>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        <span className="badge badge-info">{u.sites.length}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted" title={u.last_login_at ?? ''}>
                        {relativeTime(u.last_login_at)}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted" title={u.created_at ?? ''}>
                        {relativeTime(u.created_at)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {creator ? (
                          <div>
                            <div className="text-ink-primary">{creator.full_name || creator.email.split('@')[0]}</div>
                            <div className="text-[10px] text-ink-muted">{creator.email}</div>
                          </div>
                        ) : u.created_by_user_id ? (
                          <span className="font-mono text-ink-muted">{u.created_by_user_id.slice(0, 8)}</span>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <ActionMenu label={`Actions for ${u.email}`}>
                          {u.locked_at ? (
                            <ActionItem icon={Unlock} onClick={() => unlockMut.mutate(u.user_id)}>
                              Unlock
                            </ActionItem>
                          ) : (
                            <ActionItem icon={Lock} onClick={() => lockMut.mutate(u.user_id)}>
                              Lock
                            </ActionItem>
                          )}
                          {u.status === 'disabled' ? (
                            <ActionItem icon={Power} onClick={() => enableMut.mutate(u.user_id)}>
                              Enable account
                            </ActionItem>
                          ) : (
                            <ActionItem
                              icon={PowerOff}
                              onClick={() => {
                                if (window.confirm(`Disable ${u.email}?\nThe user will no longer be able to log in.`)) {
                                  disableMut.mutate(u.user_id);
                                }
                              }}
                            >
                              Disable account
                            </ActionItem>
                          )}
                          <ActionItem icon={Key} onClick={() => resetPwMut.mutate(u.email)}>
                            Send password reset
                          </ActionItem>
                          <ActionItem
                            icon={u.mfa_enrolled ? ShieldOff : ShieldCheck}
                            onClick={() => {
                              if (window.confirm(`Reset MFA for ${u.email}?\nThey'll re-enrol the authenticator on next login.`)) {
                                resetMfaMut.mutate(u.user_id);
                              }
                            }}
                          >
                            Reset MFA
                          </ActionItem>
                          <ActionItem
                            icon={ScrollText}
                            onClick={() => navigate(`/admin/audit?user_id=${u.user_id}`)}
                          >
                            View audit
                          </ActionItem>
                          <ActionItem
                            icon={Trash2}
                            danger
                            onClick={() => setDeletingUser({ user: u, confirm_email: '' })}
                          >
                            Delete user…
                          </ActionItem>
                        </ActionMenu>
                      </td>
                    </tr>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <tr className="border-b border-surface-border bg-surface-muted/50">
                        <td></td>
                        <td colSpan={10} className="px-3 py-3 text-xs space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <div className="font-semibold text-ink-primary mb-1">Account</div>
                              <div>User ID: <span className="font-mono">{u.user_id}</span></div>
                              <div>Created: {u.created_at ? fmtDateTime(u.created_at) : '—'}</div>
                              <div>Created by: {creator ? `${creator.full_name || creator.email}` : (u.created_by_user_id ? <span className="font-mono">{u.created_by_user_id.slice(0, 8)}</span> : '—')}</div>
                              <div>Last login: {u.last_login_at ? fmtDateTime(u.last_login_at) : '—'}</div>
                              <div>Email verified: {u.email_verified_at ? fmtDateTime(u.email_verified_at) : (verified ? 'Yes (active login)' : 'No')}</div>
                              <div>MFA enrolled: {u.mfa_enrolled ? 'Yes' : 'No'}</div>
                            </div>
                            <div>
                              <div className="font-semibold text-ink-primary mb-1">Lock / Status</div>
                              <div>Status: <span className="font-medium">{humanStatus(u.status)}</span></div>
                              {u.locked_at && (
                                <>
                                  <div>Locked at: {fmtDateTime(u.locked_at)}</div>
                                  <div>Reason: <span className="font-mono">{u.lock_reason ?? '—'}</span></div>
                                </>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-ink-primary mb-1">All site assignments</div>
                            {u.sites.length === 0 ? (
                              <span className="italic text-ink-muted">No memberships</span>
                            ) : (
                              <ul className="ml-4 list-disc">
                                {u.sites.map((s) => (
                                  <li key={s.site_id}>
                                    {sitesById.get(s.site_id) ?? s.site_id} — <span className="font-medium">{s.role ? humanRole(s.role) : 'No role'}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Assign-to-site / promote-to-platform modal */}
      {addingSite && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (addingSite.role === 'platform_admin' || addingSite.site_id) addSiteMut.mutate();
            }}
            className="card w-full max-w-md space-y-4 p-6"
          >
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">
                {addingSite.role === 'platform_admin' ? 'Promote to Platform Admin' : 'Assign user to site'}
              </h2>
              <button type="button" onClick={() => setAddingSite(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Target: <strong className="text-ink-primary">{addingSite.user.email}</strong>.
              No invitation email is sent — the new access is live immediately.
            </p>
            <div>
              <label className="label text-xs">Role</label>
              <select
                className="input"
                value={addingSite.role}
                onChange={(e) =>
                  setAddingSite({
                    ...addingSite,
                    role: e.target.value as AssignRole,
                    site_id: e.target.value === 'platform_admin' ? '' : addingSite.site_id,
                  })
                }
              >
                <option value="site_admin">Site Admin — manages a single site</option>
                <option value="platform_admin">Platform Admin — cross-tenant root, every site</option>
              </select>
              <p className="mt-1 text-[10px] text-ink-muted">
                {addingSite.role === 'platform_admin'
                  ? 'Adds a global delegation. The user gets cross-tenant admin access. No site is selected.'
                  : 'Adds a per-site membership plus a Site Admin delegation at the chosen site.'}
              </p>
            </div>
            {addingSite.role === 'site_admin' && (
              <div>
                <label className="label text-xs">Site</label>
                <select
                  className="input"
                  value={addingSite.site_id}
                  required
                  onChange={(e) => setAddingSite({ ...addingSite, site_id: e.target.value })}
                >
                  <option value="" disabled>Pick a site…</option>
                  {sites.data?.sites
                    .filter((s) => !addingSite.user.sites.some((existing) => existing.site_id === s.site_id))
                    .map((s) => (<option key={s.site_id} value={s.site_id}>{s.name}</option>))}
                </select>
                <p className="mt-1 text-[10px] text-ink-muted">
                  Sites the user already belongs to are hidden.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setAddingSite(null)}>Cancel</button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  addSiteMut.isPending ||
                  (addingSite.role === 'site_admin' && !addingSite.site_id)
                }
              >
                {addSiteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {addingSite.role === 'platform_admin' ? 'Promote' : 'Assign'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change role modal (existing) */}
      {roleEdit && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={(e) => { e.preventDefault(); changeRoleMut.mutate(); }}
            className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Change role</h2>
              <button type="button" onClick={() => setRoleEdit(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Changing role for <strong className="text-ink-primary">{roleEdit.user.email}</strong> at{' '}
              <strong className="text-ink-primary">{sitesById.get(roleEdit.site_id) ?? roleEdit.site_id}</strong>.
            </p>
            <div>
              <label className="label text-xs">New role</label>
              <select className="input" value={roleEdit.role}
                onChange={(e) => setRoleEdit({ ...roleEdit, role: e.target.value })}>
                {/* Change role: invitable roles plus Monitor (shared with the BE). */}
                {SITE_CHANGE_ROLES.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setRoleEdit(null)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={changeRoleMut.isPending}>
                {changeRoleMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete user modal (new) */}
      {deletingUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={(e) => { e.preventDefault(); if (deletingUser.confirm_email === deletingUser.user.email) removeMut.mutate(); }}
            className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-danger-700">Delete user</h2>
              <button type="button" onClick={() => setDeletingUser(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              This permanently deletes <strong className="text-ink-primary">{deletingUser.user.email}</strong>{' '}
              and cascades their memberships, delegations, invitations, MFA credentials, OTP codes,
              and access logs. The audit trail is retained.
              <br /><br />
              Type the email to confirm.
            </p>
            <input type="text" required className="input"
              placeholder={deletingUser.user.email}
              value={deletingUser.confirm_email}
              onChange={(e) => setDeletingUser({ ...deletingUser, confirm_email: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setDeletingUser(null)}>Cancel</button>
              <button type="submit" className="btn-danger"
                disabled={removeMut.isPending || deletingUser.confirm_email !== deletingUser.user.email}>
                {removeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
